import { forwardRef, useState, useEffect, ChangeEvent, FocusEvent } from 'react';
import clsx from 'clsx';
import { formatNumberSwiss, parseSwissNumber } from '@utils/number-format';

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  value?: string | number;
  onChange?: (e: { target: { value: number } }) => void;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ label, error, helperText, required, className, id, onChange, onBlur, value: propValue, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Update display value when prop value changes (only when not focused)
    useEffect(() => {
      if (!isFocused) {
        if (propValue !== undefined && propValue !== null && propValue !== '' && propValue !== 0) {
          setDisplayValue(formatNumberSwiss(propValue));
        } else {
          setDisplayValue('');
        }
      }
    }, [propValue, isFocused]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow empty string
      if (inputValue === '') {
        setDisplayValue('');
        onChange?.({ target: { value: 0 } });
        return;
      }

      // Keep the display value as entered (with apostrophes)
      setDisplayValue(inputValue);

      // Parse and send numeric value to parent
      const numericValue = parseSwissNumber(inputValue);
      onChange?.({ target: { value: numericValue } });
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      // Format the value nicely on blur
      if (displayValue) {
        const numericValue = parseSwissNumber(displayValue);
        if (numericValue > 0) {
          setDisplayValue(formatNumberSwiss(numericValue));
        }
      }

      // Call original onBlur if provided
      onBlur?.(e);
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="text"
            inputMode="numeric"
            className={clsx(
              'block w-full px-3 py-2 border rounded-md shadow-sm',
              'placeholder-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
              'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500',
              'dark:disabled:bg-gray-700',
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 dark:border-gray-600',
              className,
            )}
            required={required}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            {...props}
          />
        </div>
        {helperText && !error && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';
